import { Injectable } from '@angular/core';
import { CustomerListsService } from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CAAction } from '../actions';
import { CustomerLists } from './customer-lists.actions';
import { Store } from '@ngrx/store';
import { State, getSelectedId } from '..';
import * as selectors from '../reducers/index';
import { Router } from '@angular/router';
import { CUSTOMER_MEMBER_ROLE, FAMILY_ROLE } from '@careapp-group/core';

@Injectable()
export class CustomerListsEffects {
  paginate = createEffect((): any =>
    this.actions$.pipe(
      ofType<CAAction>(CustomerLists.PAGE_CHANGE),
      map((action) => CustomerLists.RequestAllAction())
    )
  );

  updateFilter = createEffect((): any =>
    this.actions$.pipe(
      ofType<CAAction>(CustomerLists.UPDATE_FILTER),
      map((action) => CustomerLists.RequestPageAction())
    )
  );

  updatePagination = createEffect(
    (): any =>
      this.actions$.pipe(
        ofType<CAAction>(CustomerLists.RESET_PAGE, CustomerLists.REQUEST_PAGE),
        tap((action) => {
          this.store.dispatch(CustomerLists.RequestAllAction());
        })
      ),
    {
      dispatch: false,
    }
  );

  requestCustomerLists$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(CustomerLists.REQUEST_ALL),

      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(selectors.getPageination(selectors.CUSTOMER_LISTS)),
        this.store.select(selectors.getFilter(selectors.CUSTOMER_LISTS)),
        this.store.select(selectors.org.currentOrgRoles)
      ),
      filter(
        ([action, orgId, pagination = {}, filter, orgRoles]) =>
          orgId != null &&
          !(orgRoles ?? []).some((role) => role == CUSTOMER_MEMBER_ROLE || role == FAMILY_ROLE)
      ),
      switchMap(([action, organisationId, pagination = {}, filter]) =>
        this.customerListService
          .getCustomerLists(
            {
              organisationId,
              skip: pagination.pageSize * pagination.pageIndex,
              limit: pagination.pageSize,
              name: filter.name,
            },
            'response'
          )
          .pipe(
            switchMap((response) => {
              return [
                CustomerLists.ResponseSuccessAllAction(response.body),
                CustomerLists.ResponseSuccessCountAction(
                  parseInt(response?.headers?.get('x-total-count') ?? '0')
                ),
              ];
            }),
            catchError((error) => of(CustomerLists.ResponseFailAllAction({ error })))
          )
      )
    )
  );
  requestCustomerList$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(CustomerLists.REQUEST_BY_ID),
      switchMap((action) =>
        this.customerListService.getCustomerList({ id: action.payload.id }).pipe(
          map((customerList) => {
            return CustomerLists.ResponseSuccessByIdAction(customerList);
          }),
          catchError((error) => of(CustomerLists.ResponseFailByIdAction(error)))
        )
      )
    )
  );
  createCustomerList$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(CustomerLists.REQUEST_CREATE),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, organisationId]) =>
        this.customerListService
          .createCustomerList({
            createCustomerListDto: {
              organisationId,
              customerIds: action.payload.customerIds,
              name: action.payload.name,
            },
          })
          .pipe(
            map((response) => CustomerLists.ResponseSuccessCreateAction(response)),
            catchError((error) => of(CustomerLists.ResponseFailCreateAction({ error })))
          )
      )
    )
  );

  patchCustomerList$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(CustomerLists.RequestPatchById),
      switchMap((action) =>
        this.customerListService
          .updateCustomerList({
            id: action.customerListId,
            updateCustomerListDto: {
              customerIds: action.updatedCustomerIds,
              name: action.name,
            },
          })
          .pipe(
            map((response) =>
              CustomerLists.ResponseSuccessPatchById({
                payload: response,
              })
            ),
            catchError((error) =>
              of(CustomerLists.ResponseFailPatchById({ id: action.customerListId, error }))
            )
          )
      )
    )
  );

  deleteCustomerList$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(CustomerLists.REQUEST_DESTROY_BY_ID),
      withLatestFrom(this.store.select(getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, organisationId]) =>
        this.customerListService.removeCustomerList({ id: action.payload }).pipe(
          map((response) =>
            CustomerLists.ResponseSuccessDestroyByIdAction({
              payload: response,
            })
          ),
          catchError((error) => of(CustomerLists.ResponseFailDestroyByIdAction(error)))
        )
      )
    )
  );

  constructor(
    private store: Store<State>,
    private actions$: Actions<CAAction>,
    private customerListService: CustomerListsService,
    private router: Router
  ) {}
}
